import { Navigate } from "react-router-dom";
import type { ComponentType } from "react";
import type { RouteRecord } from "vite-react-ssg";
import { ThemeProvider } from "./lib/theme";
import { ScrollToTop } from "./components/ScrollToTop";
import { ReferralLinkForwarder } from "./components/ReferralLinkForwarder";
import { SEO } from "./components/SEO";
import Landing from "./pages/Landing";
import { ManagedLandingPage } from "./components/ManagedLandingPage";
import Guides from "./pages/guides/Guides";
import { StackDeploymentGuide } from "./pages/guides/StackDeploymentGuide";
import { STACK_GUIDES } from "./data/stackGuides";

type PageModule = { default: ComponentType };

const lazyPage =
  (importer: () => Promise<PageModule>) =>
    async () => {
      const mod = await importer();
      return { Component: mod.default };
    };

function RedirectWithSEO({
  to,
  title,
  description,
}: {
  to: string;
  title: string;
  description: string;
}) {
  return (
    <>
      <SEO title={title} description={description} robots="noindex, follow" canonicalPath={to.startsWith("/") ? to : undefined} />
      <Navigate to={to} replace />
    </>
  );
}

/**
 * Root layout shared by every route. Lives inside the router (created by
 * vite-react-ssg), so the SEO/ScrollToTop hooks that read `useLocation` work
 * during both prerender and client hydration.
 */
function Layout() {
  return (
    <ThemeProvider>
      <ScrollToTop />
      <ReferralLinkForwarder />
      <ManagedLandingPage />
    </ThemeProvider>
  );
}

/**
 * Route table consumed by vite-react-ssg. Every path here is static (no params),
 * so the SSG step prerenders each one to its own HTML file at build time. The
 * status page moved to gerdoocloudstatus.ir; Caddy redirects the
 * old /support/status path there.
 */
export const routes: RouteRecord[] = [
  {
    path: "/",
    element: <Layout />,
    children: [
      { index: true, element: <Landing /> },
      { path: "pricing", lazy: lazyPage(() => import("./pages/Pricing")) },
      { path: "features", lazy: lazyPage(() => import("./pages/Features")) },
      { path: "datacenters", lazy: lazyPage(() => import("./pages/Datacenters")) },

      // Cloud provider comparison pages
      { path: "compare/liara", lazy: lazyPage(() => import("./pages/compare/Liara")) },
      { path: "compare/chabokan", lazy: lazyPage(() => import("./pages/compare/Chabokan")) },
      { path: "compare/parspack", lazy: lazyPage(() => import("./pages/compare/ParsPack")) },
      { path: "compare/arvancloud", lazy: lazyPage(() => import("./pages/compare/ArvanCloud")) },
      { path: "compare/best-cloud-container-iran", lazy: lazyPage(() => import("./pages/compare/BestCloudContainerIran")) },

      // Answer-focused guides: canonical responses to common Persian queries.
      { path: "guides", element: <Guides /> },
      { path: "guides/deploy-monorepo", lazy: lazyPage(() => import("./pages/guides/DeployMonorepo")) },
      { path: "guides/deploy-python", lazy: lazyPage(() => import("./pages/guides/DeployPython")) },
      ...STACK_GUIDES.map((guide) => ({
        path: `guides/deploy-${guide.slug}`,
        element: <StackDeploymentGuide guide={guide} />,
      })),

      { path: "info/about", lazy: lazyPage(() => import("./pages/info/About")) },

      { path: "legal/terms", lazy: lazyPage(() => import("./pages/legal/Terms")) },
      { path: "legal/privacy", lazy: lazyPage(() => import("./pages/legal/Privacy")) },
      { path: "legal/sla", lazy: lazyPage(() => import("./pages/legal/SLA")) },

      { path: "contact", lazy: lazyPage(() => import("./pages/support_info/Contact")) },
      // NOTE: /support/docs is NOT a SPA route anymore. The docs are a separate
      // Nextra static site (landing/docs-site) served by nginx under
      // /support/docs (dev: vite proxy to the docs container, see vite.config.ts).

      // Marketplace Routes
      { path: "marketplace", lazy: lazyPage(() => import("./pages/marketplace/Marketplace")) },
      { path: "marketplace/apps/postgresql", lazy: lazyPage(() => import("./pages/marketplace/apps/PostgreSQL")) },
      { path: "marketplace/apps/mysql", lazy: lazyPage(() => import("./pages/marketplace/apps/MySQL")) },
      { path: "marketplace/apps/redis", lazy: lazyPage(() => import("./pages/marketplace/apps/Redis")) },
      { path: "marketplace/apps/minio", lazy: lazyPage(() => import("./pages/marketplace/apps/MinIO")) },
      { path: "marketplace/apps/wordpress", lazy: lazyPage(() => import("./pages/marketplace/apps/WordPress")) },
      { path: "marketplace/apps/ghost", lazy: lazyPage(() => import("./pages/marketplace/apps/Ghost")) },
      { path: "marketplace/apps/drupal", lazy: lazyPage(() => import("./pages/marketplace/apps/Drupal")) },
      { path: "marketplace/apps/joomla", lazy: lazyPage(() => import("./pages/marketplace/apps/Joomla")) },
      { path: "marketplace/apps/gitea", lazy: lazyPage(() => import("./pages/marketplace/apps/Gitea")) },
      { path: "marketplace/apps/grafana", lazy: lazyPage(() => import("./pages/marketplace/apps/Grafana")) },
      { path: "marketplace/apps/metabase", lazy: lazyPage(() => import("./pages/marketplace/apps/Metabase")) },
      { path: "marketplace/apps/mattermost", lazy: lazyPage(() => import("./pages/marketplace/apps/Mattermost")) },
      { path: "marketplace/apps/jupyter", lazy: lazyPage(() => import("./pages/marketplace/apps/Jupyter")) },
      { path: "marketplace/apps/rabbitmq", lazy: lazyPage(() => import("./pages/marketplace/apps/RabbitMQ")) },
      { path: "marketplace/apps/meilisearch", lazy: lazyPage(() => import("./pages/marketplace/apps/Meilisearch")) },
      { path: "marketplace/apps/nocodb", lazy: lazyPage(() => import("./pages/marketplace/apps/NocoDB")) },
      { path: "marketplace/apps/pocketbase", lazy: lazyPage(() => import("./pages/marketplace/apps/PocketBase")) },
      { path: "marketplace/apps/appsmith", lazy: lazyPage(() => import("./pages/marketplace/apps/Appsmith")) },
      { path: "marketplace/apps/n8n", lazy: lazyPage(() => import("./pages/marketplace/apps/n8n")) },
      { path: "marketplace/apps/odoo", lazy: lazyPage(() => import("./pages/marketplace/apps/Odoo")) },
      { path: "marketplace/apps/strapi", lazy: lazyPage(() => import("./pages/marketplace/apps/Strapi")) },
      { path: "marketplace/apps/pgadmin", lazy: lazyPage(() => import("./pages/marketplace/apps/pgAdmin")) },
      { path: "marketplace/apps/umami", lazy: lazyPage(() => import("./pages/marketplace/apps/Umami")) },
      { path: "marketplace/apps/matomo", lazy: lazyPage(() => import("./pages/marketplace/apps/Matomo")) },
      { path: "marketplace/apps/uptime-kuma", lazy: lazyPage(() => import("./pages/marketplace/apps/UptimeKuma")) },
      { path: "marketplace/apps/mariadb", lazy: lazyPage(() => import("./pages/marketplace/apps/MariaDB")) },
      { path: "marketplace/apps/mongodb", lazy: lazyPage(() => import("./pages/marketplace/apps/MongoDB")) },
      { path: "marketplace/apps/memcached", lazy: lazyPage(() => import("./pages/marketplace/apps/Memcached")) },
      { path: "marketplace/apps/nats", lazy: lazyPage(() => import("./pages/marketplace/apps/NATS")) },
      { path: "marketplace/apps/kafka", lazy: lazyPage(() => import("./pages/marketplace/apps/Kafka")) },
      { path: "marketplace/apps/nginx", lazy: lazyPage(() => import("./pages/marketplace/apps/Nginx")) },
      { path: "marketplace/apps/apache", lazy: lazyPage(() => import("./pages/marketplace/apps/Apache")) },
      { path: "marketplace/apps/openclaw", lazy: lazyPage(() => import("./pages/marketplace/apps/OpenClaw")) },

      { path: "marketplace/apps/openwebui", lazy: lazyPage(() => import("./pages/marketplace/apps/OpenWebUI")) },
      { path: "marketplace/apps/qdrant", lazy: lazyPage(() => import("./pages/marketplace/apps/Qdrant")) },
      { path: "marketplace/apps/flowise", lazy: lazyPage(() => import("./pages/marketplace/apps/Flowise")) },
      { path: "marketplace/apps/supabase", lazy: lazyPage(() => import("./pages/marketplace/apps/Supabase")) },
      { path: "marketplace/apps/directus", lazy: lazyPage(() => import("./pages/marketplace/apps/Directus")) },
      { path: "marketplace/apps/gitlab", lazy: lazyPage(() => import("./pages/marketplace/apps/GitLab")) },
      { path: "marketplace/apps/zabbix", lazy: lazyPage(() => import("./pages/marketplace/apps/Zabbix")) },
      { path: "marketplace/apps/clickhouse", lazy: lazyPage(() => import("./pages/marketplace/apps/ClickHouse")) },
      { path: "marketplace/apps/pusher", lazy: lazyPage(() => import("./pages/marketplace/apps/Pusher")) },
      { path: "marketplace/apps/sentry", lazy: lazyPage(() => import("./pages/marketplace/apps/Sentry")) },
      { path: "marketplace/apps/onlyoffice", lazy: lazyPage(() => import("./pages/marketplace/apps/OnlyOffice")) },


      { path: "agents", lazy: lazyPage(() => import("./pages/products/Agents")) },
      { path: "agents/codex", lazy: lazyPage(() => import("./pages/products/AgentDetail")) },
      { path: "agents/claude-code", lazy: lazyPage(() => import("./pages/products/AgentDetail")) },
      { path: "agents/cursor", lazy: lazyPage(() => import("./pages/products/AgentDetail")) },
      { path: "agents/antigravity", lazy: lazyPage(() => import("./pages/products/AgentDetail")) },
      { path: "agents/copilot", lazy: lazyPage(() => import("./pages/products/AgentDetail")) },
      { path: "agents/opencode", lazy: lazyPage(() => import("./pages/products/AgentDetail")) },
      { path: "agents/devin", lazy: lazyPage(() => import("./pages/products/AgentDetail")) },
      { path: "agents/pi", lazy: lazyPage(() => import("./pages/products/AgentDetail")) },
      { path: "agents/grok", lazy: lazyPage(() => import("./pages/products/AgentDetail")) },
      { path: "products/databases", lazy: lazyPage(() => import("./pages/products/Databases")) },
      { path: "products/containers", lazy: lazyPage(() => import("./pages/products/Containers")) },
      { path: "products/paas", lazy: lazyPage(() => import("./pages/products/PaaS")) },
      { path: "products/dns", lazy: lazyPage(() => import("./pages/products/DNS")) },
      { path: "products/email", lazy: lazyPage(() => import("./pages/products/Email")) },
      { path: "products/python", lazy: lazyPage(() => import("./pages/products/Python")) },
      { path: "products/nodejs", lazy: lazyPage(() => import("./pages/products/Nodejs")) },


      {
        path: "products/wordpress",
        element: (
          <RedirectWithSEO
            to="/marketplace/apps/wordpress"
            title="هاست وردپرس ابری | گردو"
            description="هاست وردپرس ابری گردو با استقرار آماده از بازارچه، منابع اختصاصی PaaS، SSL، مدیریت ساده و زیرساخت پایدار برای سایت‌های وردپرسی."
          />
        ),
      },
      { path: "products/laravel", lazy: lazyPage(() => import("./pages/products/Laravel")) },
      { path: "products/go", lazy: lazyPage(() => import("./pages/products/Go")) },
      { path: "products/ruby", lazy: lazyPage(() => import("./pages/products/Ruby")) },
      { path: "products/rust", lazy: lazyPage(() => import("./pages/products/Rust")) },
      { path: "products/svelte", lazy: lazyPage(() => import("./pages/products/Svelte")) },
      { path: "products/vue", lazy: lazyPage(() => import("./pages/products/Vue")) },
      { path: "products/react", lazy: lazyPage(() => import("./pages/products/ReactLanding")) },
      { path: "products/nextjs", lazy: lazyPage(() => import("./pages/products/Nextjs")) },
      { path: "products/astro", lazy: lazyPage(() => import("./pages/products/Astro")) },
      { path: "products/static-sites", lazy: lazyPage(() => import("./pages/products/StaticSites")) },
      { path: "free-host", lazy: lazyPage(() => import("./pages/FreeHost")) },
      { path: "download", lazy: lazyPage(() => import("./pages/Download")) },
      {
        path: "free-server",
        element: (
          <RedirectWithSEO
            to="/free-host"
            title="سکوی ابری رایگان | گردو"
            description="پلن رایگان گردو برای تست و اجرای پروژه‌های کوچک ابری با منابع پایه، زیردامنه اختصاصی و شروع سریع برای توسعه‌دهندگان."
          />
        ),
      },

      // 404 — `/404` is prerendered to 404.html (served by nginx as the
      // error document with a real 404 status); the wildcard renders the same
      // page client-side for any path the SPA shell is reached with.
      { path: "404", lazy: lazyPage(() => import("./pages/NotFound")) },
      { path: "*", lazy: lazyPage(() => import("./pages/NotFound")) },
    ],
  },
];
